<template>
  <div class="card">
    <TabList
      :current-tab="currentTab"
      :tabs="tabList"
      @update:tab="updateTab"
    />
    <!-- 捐款記錄 -->
    <DonationRecord
      :current-tab="currentTab"
      :table-data="currentTableData"
      :table-columns="currentTableColumns"
      :table-options="options"
      :member-area-data="memberAreaData"
    />
    <!-- 紙本收據寄送狀態 -->
    <ReceiptStatus
      :current-tab="currentTab"
      :table-data="currentTableData"
      :table-columns="currentTableColumns"
      :table-options="options"
    />
    <!-- 信用卡資料 -->
    <CardInfo
      :current-tab="currentTab"
      :recurring-data="recurringData"
    />
    <!-- 個人資料 -->
    <PersonalInfo
      :current-tab="currentTab"
      :member-contact-data="memberContactData"
    />
    <!-- 單筆待繳款項 -->
    <PendingPayment
      :current-tab="currentTab"
      :table-data="currentTableData"
      :table-columns="currentTableColumns"
      :table-options="options"
      :member-area-data="memberAreaData"
    />
  </div>
</template>

<script>
import _ from 'lodash';
import jquery from 'jquery';
import moment from 'moment';
import { mapActions, mapState } from 'vuex';
import { mappingKey } from '@/helpers/common';
// stores
import memberState from '@/store/modules/member';
// components
import TabList from '@/components/layout/TabList';
import daterangepicker from 'daterangepicker';
import DonationRecord from '@/components/search/result/DonationRecord';
import ReceiptStatus from '@/components/search/result/ReceiptStatus';
import CardInfo from '@/components/search/result/CardInfo';
import PersonalInfo from '@/components/search/result/PersonalInfo';
import PendingPayment from '@/components/search/result/PendingPayment';

// styles
import '@/styles/06-components/_components.vue-tables.scss';

window.$ = window.jquery = jquery;
window.moment = moment;
window.daterangepicker = daterangepicker;

export default {
  components: {
    TabList,
    DonationRecord,
    ReceiptStatus,
    CardInfo,
    PersonalInfo,
    PendingPayment,
  },
  data() {
    return {
      tableData: {},
      options: {
        headings: {
          date: '日期',
          closeDate: '繳費期限',
          amount: '金額',
          status: '狀態',
          receiptName: '收據抬頭',
          receiptType: '收據類型',
          receiptNo: '收據編號',
          reissue: '', // 補發電子收據
          resend: '', // 代碼重新寄送
          paymentType: '繳費方式',
        },
        filterByColumn: true,
        filterable: false,
        dateColumns: ['date'],
        dateFormat: 'YYYY/MM/DD',
      },
      currentTab: '',
      tabList: [
        {
          name: '捐款記錄',
          id: 'donationRecord',
        },
        {
          name: '紙本收據寄送狀態',
          id: 'receiptStatus',
        },
        {
          name: '信用卡資料',
          id: 'cardInfo',
        },
        {
          name: '個人資料',
          id: 'personalInfo',
        },
        {
          name: '單筆待繳款項',
          id: 'pendingPayment',
        },
      ],
    };
  },
  computed: {
    currentTableData() {
      return this?.[this.currentTab]?.data || [];
    },
    currentTableColumns() {
      return this?.[this.currentTab]?.columns || [];
    },
    // 捐款記錄
    donationRecord() {
      const records = this.memberAreaData;
      if(_.isEmpty(records)) return [];
      const paidRecords = _.filter(records, (record) => !!record.npe01__Paid__c);
      const formattedRecords = _.map(paidRecords, (record) => ({
        receiptName: record.Invoice_Title__c, // 收據抬頭
        date: record.npe01__Opportunity__r.CloseDate, // 捐款日期
        receiptNo: (record.Receipt_Prefix__c || '') + record.Receipt_No__c, // 收據前置號 + 收據編號
        receiptType: mappingKey('conatctInvoiceType', record.npe01__Opportunity__r.Receipt_Type__c), // 收據類型
        reissue: record.Id, // 電子收據補發
        amount: record.npe01__Payment_Amount__c, // 捐款金額
      }));

      return {
        columns: ['date', 'amount', 'receiptName', 'receiptType', 'receiptNo', 'reissue'],
        data: _.filter(formattedRecords),
      };
    },
    // 紙本收據寄送狀態
    receiptStatus() {
      const records = this.memberAreaData;
      if(_.isEmpty(records)) return [];
      const paidRecords = _.filter(records, (record) => !!record.npe01__Paid__c);

      return {
        columns: ['date', 'amount', 'receiptNo', 'status'],
        data: _.map(paidRecords, record => ({
          date: record.npe01__Opportunity__r.CloseDate, // 日期
          amount: record.npe01__Payment_Amount__c, // 金額
          receiptNo: record.Receipt_No__c, // 收據編號
          status: mappingKey('Receipt_Send_Status__c', record.Receipt_Send_Status__c) || '不需要', // 寄送狀態
        })),
      };
    },
    // 單筆待繳款項
    // (原 key: donate_records_single_unpaid)
    pendingPayment() {
      const records = this.memberAreaData;
      if(_.isEmpty(records)) return [];

      const checkPendingRecord = (record) => (
        record.npe01__Opportunity__r.Probability !== 100
        && !record.npe01__Opportunity__r.isRecurringDonation__c
        && (record.ECPAY_Order_Type__c === 'CVS' || record.ECPAY_Order_Type__c === 'ATM')
        && moment(record.npe01__Opportunity__r.CloseDate).isAfter(new Date())
      );

      return {
        columns: ['closeDate', 'amount', 'paymentType', 'resend'],
        data: _.filter(_.map(records, record => {
          return checkPendingRecord(record)
            ? {
              closeDate: moment(record.npe01__Opportunity__r.CloseDate).subtract(1, 'days').format('YYYY-MM-DD'), // 繳費期限，ref: https://git.io/JLB50
              amount: record.npe01__Payment_Amount__c, // 金額
              paymentType: mappingKey('oppoOrderType', record.ECPAY_Order_Type__c), // 繳費方式
              resend: record.Id, // 代碼重新寄送
            }
            : null;
        })),
      };
    },
    // 定期定額授權資料
    recurringData() {
      const lastRecurringData = this.memberRecurringData?.[0]?.[0];
      if(!lastRecurringData) return {};
      const cardNoString = lastRecurringData.recurDonationCreditCardNo;
      const cardExpireString = lastRecurringData.recurDonationCreditCardExpire; // MMYY
      return {
        amount: lastRecurringData.recurDonationInstallmentAmount, // 每期扣款金額
        cardNo: `**** **** **** ${ cardNoString.slice(12) }`, // 信用卡號碼末四碼
        cardExpire: `${ cardExpireString.slice(2, 4) }-${ cardExpireString.slice(0, 2) }`, // 到期年月: YY-MM
        nextPaymentDate: lastRecurringData.recurDonationNextPaymentDate, // 下次扣款日期
      };
    },
    ...mapState('member', [
      'memberAreaData',
      'memberContactData',
      'memberRecurringData',
    ]),
  },
  watch: {
    '$route.query.tab'(tabId) {
      if(!tabId) {
        return;
      }
      this.currentTab = tabId;
    },
    currentTab(tabId) {
      if(!tabId) return;
      if(!this.shouldInitTable(tabId)) return;
      if(_.isEmpty(this.memberAreaData)) return;
      if(_.isEmpty(this.tableData[tabId])) {
        this.tableData[tabId] = this.initTableData(tabId);
      }
    },
  },
  beforeCreate() {
    if(this.$store.state.member) {
      return;
    }
    this.$store.registerModule('member', memberState);
  },
  created() {
    this.currentTab = this.$route.query.tab;
  },
  async mounted() {
    this.currentTab = this.$route.query.tab;
    const records = await this.getMemberAreaData();
    if(_.isEmpty(records)) return;
    const tableId = this.currentTab;
    if(this.shouldInitTable(tableId)) {
      this.tableData[tableId] = this.initTableData(tableId);
    }

    await this.getContactData(true);
    await this.getRecurringDonations();
  },
  methods: {
    updateTab(tabId) {
      this.$router.push({
        query: { tab: tabId },
      });
    },
    initTableData(tableId) {
      const tableData = this.getTableDataById(tableId);

      const columns = tableData.columns;
      const data = tableData.data.map(x => {
        x.date = moment(x.date);
        return x;
      });
      return {
        columns,
        data,
      };
    },
    shouldInitTable(tabId) {
      return _.includes([
        'donationRecord',
        'receiptStatus',
        'pendingPayment',
      ], tabId);
    },
    getTableDataById(id) {
      switch (id) {
        case 'donationRecord':
          return this.donationRecord;
        case 'receiptStatus':
          return this.receiptStatus;
        case 'pendingPayment':
          return this.pendingPayment;
        default:
          return [];
      }
    },
    ...mapActions('member', [
      'getMemberAreaData',
      'getContactData',
      'getRecurringDonations',
    ]),
  },
};
</script>

<style lang="scss" module>
.link {
  color: $red;
}

.listTitle {
  font-weight: 500;
  border-bottom: 1px solid #d8d8d8;
  margin-top: 34px;
  padding-bottom: 1em;
}
</style>
